import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import SEOHead, { SITE_URL } from '../components/SEOHead';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const fetchService = async () => {
    try {
      const response = await api.get(`/services/${slug}/`);
      setService(response.data);
    } catch (err) {
      setError('Failed to load service');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    // Scroll window to top
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    
    // Also scroll the content container to top (for mobile)
    const contentContainer = document.querySelector('.service-detail-content');
    if (contentContainer) {
      contentContainer.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [slug]);

  // Auto-slide functionality - moved before early returns to follow Rules of Hooks
  useEffect(() => {
    if (!service) return;
    
    const allPhotos = (service.extra_photos || []).map(photo => ({
      url: photo.photo,
      caption_en: photo.caption_en,
      caption_ka: photo.caption_ka,
      isMain: false
    }));

    if (allPhotos.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === allPhotos.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [service]);

  const getLocalizedField = (field) => {
    if (!service) return '';
    const langField = `${field}_${i18n.language}`;
    return service[langField] || service[`${field}_en`];
  };

  if (loading) {
    return <div className="container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (error || !service) {
    return <div className="container"><div className="error-message">{error || 'Service not found'}</div></div>;
  }

  const allPhotos = (service.extra_photos || []).map(photo => ({
    url: photo.photo,
    caption_en: photo.caption_en,
    caption_ka: photo.caption_ka,
    isMain: false
  }));

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === allPhotos.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? allPhotos.length - 1 : prevIndex - 1
    );
  };

  const localizedTitle = (getLocalizedField('title') || '').trim();
  const localizedLongDescription = (getLocalizedField('long_description') || '').trim();
  const localizedShortDescription = (getLocalizedField('short_description') || '').trim();
  const hasRightContent = localizedTitle.length > 0 || localizedLongDescription.length > 0;

  const seoTitle = (getLocalizedField('meta_title') || '').trim();
  const seoDescription = (getLocalizedField('meta_description') || '').trim();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: localizedTitle || 'GFM Studio Service',
      description: localizedShortDescription || localizedLongDescription.slice(0, 155),
      provider: { '@type': 'Organization', name: 'GFM Studio', url: SITE_URL },
      url: `${SITE_URL}/services/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Services', item: `${SITE_URL}/services` },
        { '@type': 'ListItem', position: 2, name: localizedTitle || slug, item: `${SITE_URL}/services/${slug}` },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={seoTitle || (localizedTitle ? `${localizedTitle} | GFM Studio` : 'GFM Studio')}
        description={seoDescription || localizedShortDescription || localizedLongDescription.slice(0, 155) || 'Production services from GFM Studio.'}
        path={`/services/${slug}`}
        image={service.og_image || service.main_photo || undefined}
        type="website"
        noindex={service.robots_index === false}
        jsonLd={jsonLd}
      />
      <div className={`service-detail-split-container ${!hasRightContent ? 'image-only' : ''}`}>
        {/* Left Section - Image Slider */}
        <div className="service-detail-left-section">
          <div className="service-carousel-container">
            <div className="service-carousel-main">
              <div className="service-carousel-track">
                {allPhotos.length > 0 ? (
                  allPhotos.map((photo, index) => (
                    <div
                      key={index}
                      className={`service-carousel-slide ${index === currentImageIndex ? 'active' : ''}`}
                    >
                      <img
                        src={photo.url}
                        alt={i18n.language === 'ka' ? photo.caption_ka : photo.caption_en}
                        className="service-carousel-image"
                      />
                    </div>
                  ))
                ) : (
                  <div className="service-carousel-slide active">
                    <div className="no-images-placeholder">
                      <p>{i18n.language === 'ka' ? 'გალერეის ფოტოები არ არის' : 'No gallery images available'}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {allPhotos.length > 1 && (
                <>
                  <button
                    type="button"
                    className="service-carousel-btn service-carousel-prev"
                    onClick={prevImage}
                    aria-label={i18n.language === 'ka' ? 'წინა ფოტო' : 'Previous photo'}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="service-carousel-btn service-carousel-next"
                    onClick={nextImage}
                    aria-label={i18n.language === 'ka' ? 'შემდეგი ფოტო' : 'Next photo'}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                    </svg>
                  </button>

                  <div className="service-carousel-indicators">
                    {allPhotos.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`service-carousel-indicator ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(index)}
                        aria-label={`${i18n.language === 'ka' ? 'ფოტო' : 'Photo'} ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Section - Content */}
        {hasRightContent && (
          <div className="service-detail-right-section">
            <div className="service-detail-content">
              {localizedTitle && (
                <h1 className="service-detail-title">{localizedTitle}</h1>
              )}

              {localizedLongDescription && (
                <div className="service-detail-long-desc">
                  <h2>{i18n.language === 'ka' ? 'დეტალური აღწერა' : 'Detailed Description'}</h2>
                  <div className="service-description-content">
                    {localizedLongDescription.split('\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ServiceDetail;

